import React from 'react';
import { motion } from 'framer-motion';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
  icon?: string;
  variant?: 'primary' | 'secondary' | 'ghost';
}

interface EmptyStateProps {
  icon: string;
  title: string;
  description: string;
  actions?: EmptyStateAction[];
  illustration?: 'series' | 'tasks' | 'pages' | 'projects' | 'characters' | 'notes' | 'generic';
}

const actionVariantClasses: Record<string, string> = {
  primary: 'btn btn-primary',
  secondary: 'btn btn-secondary',
  ghost: 'btn btn-ghost',
};

const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  actions = [],
  illustration = 'generic',
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="flex flex-col items-center justify-center py-16 px-8"
    >
      {/* Icon */}
      <div className="w-16 h-16 flex items-center justify-center border border-outline-variant mb-6 bg-surface-container">
        <span className="material-icons text-3xl text-on-surface-variant/30">{icon}</span>
      </div>

      {/* Title */}
      <h3 className="text-lg font-display font-bold text-on-surface mb-2 text-center">
        {title}
      </h3>

      {/* Description */}
      <p className="text-on-surface-variant/50 text-sm max-w-sm text-center leading-relaxed mb-8">
        {description}
      </p>

      {/* Action buttons */}
      {actions.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-3">
          {actions.map((action, idx) => (
            <button
              key={idx}
              onClick={action.onClick}
              className={`${actionVariantClasses[action.variant || 'primary']} gap-2`}
            >
              {action.icon && (
                <span className="material-icons text-sm">{action.icon}</span>
              )}
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* Decorative bottom line */}
      <div className="mt-12 flex items-center gap-2">
        <div className="w-8 h-px bg-outline-variant/20" />
        <div className="w-1.5 h-1.5 bg-outline-variant/15" />
        <div className="w-8 h-px bg-outline-variant/20" />
      </div>
    </motion.div>
  );
};

export default EmptyState;